import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { type Color, colors, normaliseColor } from "../../shared/colors";
import { sharedMutators } from "../../shared/mutators";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { playShuffle } from "../lib/sounds";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { Button } from "./Button";
import { CircleMesh, shuffleColors } from "./CircleMesh";

function playShuffleSound() {
  playShuffle();
}

const Popover = styled(AnchoredPopover)`
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 40px 40px 16px 16px;
  padding: 8px;
  z-index: ${zIndexes.dropdown};
`;

const PreviewContainer = styled("div")`
  width: 128px;
  height: 128px;
  border-radius: 32px;
  overflow: hidden;
`;

const ButtonRow = styled("div")`
  display: flex;
  justify-content: center;
  gap: 4px;
`;

type Props = {
  workflowId: string;
  currentBadgeSeed: string | null;
  isOpen: boolean;
  onClose: () => void;
  anchorName: string;
};

type BadgeConfig = {
  colors: Color[];
  seed: string;
};

export function parseBadgeConfig(
  badgeSeed: string | null,
  workflowId: string
): BadgeConfig {
  if (!badgeSeed) {
    return { colors: [...colors], seed: workflowId };
  }
  try {
    const parsed = JSON.parse(badgeSeed);
    const rawColors: string[] = parsed.colors ?? [...colors];
    return {
      colors: rawColors.map(normaliseColor) as Color[],
      seed: parsed.seed ?? workflowId,
    };
  } catch {
    return { colors: [...colors], seed: badgeSeed };
  }
}

export function CircleMeshEditor({
  workflowId,
  currentBadgeSeed,
  isOpen,
  onClose,
  anchorName,
}: Props) {
  const z = useZero();
  const initialConfig = parseBadgeConfig(currentBadgeSeed, workflowId);
  const [previewColors, setPreviewColors] = useState<Color[]>(
    initialConfig.colors
  );
  const [previewSeed, setPreviewSeed] = useState(initialConfig.seed);

  const [containerRef, portalRef] = useMouseDownOutside(onClose);
  const wasOpen = useRef(false);

  // Sync editor state when the badge changes externally
  useEffect(() => {
    const config = parseBadgeConfig(currentBadgeSeed, workflowId);
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setPreviewColors(config.colors);
    setPreviewSeed(config.seed);
  }, [currentBadgeSeed, workflowId]);

  // Play sound on open
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      playShuffleSound();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    z.mutate(
      sharedMutators.updateWorkflowBadge({
        workflowId,
        badgeSeed: JSON.stringify({ colors: previewColors, seed: previewSeed }),
      })
    );
    onClose();
  };

  const handleShuffle = () => {
    playShuffleSound();
    setPreviewSeed(nanoid());
    setPreviewColors(shuffleColors());
  };

  const popoverStyle = {
    bottom: "anchor(top)",
    left: "anchor(center)",
    transform: "translateX(-50%)",
    marginBottom: 4,
    positionTryFallbacks: "flip-block",
  } as React.CSSProperties;

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      <Popover
        isOpen={isOpen}
        popoverRef={portalRef}
        target={anchorName || undefined}
        style={popoverStyle}
        trigger={(props) => <div {...props} />}
      >
        {/* Stop propagation to prevent Link navigation via React's synthetic event bubbling */}
        <div
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <PreviewContainer>
            <CircleMesh
              key={previewSeed}
              targetCircleSize={64}
              seed={previewSeed}
              mutingIntensity={0}
              animate
              animateFade={false}
              animateAll
              restrictedColors={previewColors}
            />
          </PreviewContainer>
          <ButtonRow>
            <Button variant="secondary" size="l" icon="x" onClick={onClose} />
            <Button
              variant="primary"
              size="l"
              icon="dices"
              onClick={handleShuffle}
            />
            <Button
              variant="secondary"
              size="l"
              icon="check"
              onClick={handleConfirm}
            />
          </ButtonRow>
        </div>
      </Popover>
    </div>
  );
}
